import { TrainingVideo } from 'admin/interfaces'
import { impersonationSelector } from 'admin/services/selectors'
import { createSelector } from 'reselect'
import { GET_STARTED_TASKS } from 'routes/trial/interfaces'
import { User } from 'shared/types'
import { arrayToRecord } from 'shared/utils/array'
import StoreState from 'store/state'
import { sortByKeyAscending } from 'utils/sort/order'

export const usersSelector = (state: StoreState) => state.user
export const sliceUsersCurrent = createSelector(usersSelector, (state) => state.current)
export const currentUserSelector = createSelector(sliceUsersCurrent, impersonationSelector,
  (current, impersonate) => {
    if (!current) {
      return null
    }

    if (impersonate && impersonate.userId) {
      return Object.assign({}, current, { userId: impersonate.userId, productInfo: impersonate.productInfo })
    }

    return current
  }
)
export const isBetaUserSelector = createSelector(currentUserSelector, (user: User | null) => user?.roles?.isBeta || false)
export const manualInsightsRefreshEnabledSelector = createSelector(
  currentUserSelector,
  (user: User | null) => Boolean(user?.roles?.manualInsightsRefresh)
)

export const isTrialActiveSelector = createSelector(currentUserSelector, user => {
  return user?.account.trial === 'active'
})

export const trialEndsAtSelector = createSelector(currentUserSelector, user => {
  return user?.account.trialEndsAt
})
export const isTrialExpiredSelector = createSelector(currentUserSelector, user => {
  return user?.account.trial === 'expired' && !user?.account.hasPaymentInfo
})

export const userProductSelector = createSelector(currentUserSelector, (user: User) => user && user.productInfo)
export const userProductDetailsSelector = createSelector(currentUserSelector, user => user?.account.productDetails)
export const defaultPostToSelector = createSelector(currentUserSelector, (user) => user && user.defaultPostTo.split(',') || [])

export const isAuthenticated = createSelector(currentUserSelector, (user: User | null) => {
  if (!user || !user.accessToken) {
    return false
  }

  return true
})

export const lastLoginTimeSelector = createSelector(currentUserSelector, user => user?.lastLoginTime)

export const accessTokenSelector = createSelector(currentUserSelector, impersonationSelector, (user, impersonation) => {
  if (impersonation && impersonation.token) {
    return impersonation.token
  }
  return user && user.accessToken
})

export const userIsAdminSelector = createSelector(currentUserSelector, user => ['1', 1].includes(user?.account?.masterAdmin as any))
export const trialTasksSelector = createSelector(usersSelector, state => {
  return [...state.trialTasks].sort(sortByKeyAscending('order'))
})
export const trainingVideosSelector = createSelector(usersSelector, state => state.trainingVideos)
export const userSubscriptionsSelector = createSelector(usersSelector, state => state.subscription)
export const userPlanSubscriptionSelector = createSelector(currentUserSelector, userSubscriptionsSelector, (user, subs) => {
  const handle = (item: any) => item?.price?.product?.metadata?.handle
  const items = subs?.items?.data || []
  const planHandle = user?.account.productDetails?.handle
  return items.find((i: any) => handle(i) === planHandle)
    || items.find((i: any) => !handle(i)?.startsWith('addon-'))
    || null
})
// When the plan next bills, in seconds. Add-ons bill with the plan, so they renew or end on this date too.
export const userNextBillingTimestampSelector = createSelector(
  userPlanSubscriptionSelector,
  (subscription): number | undefined => subscription?.currentPeriodEndActual || subscription?.currentPeriodEnd
)
export const getStartedTasksSelector = createSelector(trialTasksSelector, trainingVideosSelector, (tasks, sections) => {
  const videosMap = sections.map(s => s.videos || []).flat().reduce((acc, video) => {
    if (video.taskCode) {
      acc[video.taskCode] = video
    }
    return acc
  }, {} as Record<string, TrainingVideo>)
  return tasks.filter(t => GET_STARTED_TASKS.includes(t.code)).map(t => {
    return { ...t, title: videosMap[t.code]?.title }
  })
})
export const trialTasksRecordSelector = createSelector(trialTasksSelector, tasks => arrayToRecord(tasks, 'code'))
